import { type FC } from 'react';
import { Flex, Grid, Section, SectionHeader } from '@highereducation/hen-ui';

import { Card } from '../../Card';
import { SponsoredResultsPopover } from '../../SponsoredResultsPopover';
import type { Result } from '../../../types/result';
import { EventingLocation } from '../../../types/enums';
import { useResultsPageConfigOptional } from '../../../providers/ResultsPageConfigProvider';

type Props = {
  results: Result[];
  subject?: string;
  onProgramClick: (result: Result) => void;
  submittedResults?: { programId?: number; schoolId?: string }[];
  showPopupExperience?: boolean;
  /**
   * Forwards to the inner `Card` so the embedded recommender rail
   * participates in the page-level `ComparisonProvider`. Defaults to `true`
   * to mirror `ResultList`; pass `false` for surfaces where compare should
   * be suppressed.
   */
  showComparisonCheckbox?: boolean;
  /**
   * Lead / partner-identifier sources for the post-submit Apply CTA
   * interpolator (EDUN-14483). Forwarded to every `Card`.
   */
  lead?: object;
  partnerIdentifiers?: Record<string, string>;
};

/**
 * Highlighted embedded recommender on the results page.
 * Hen-ui Section + SectionHeader; up to two DegreeCardDetailed cards via Card.
 */
const EmbeddedRecommender: FC<Props> = ({
  results,
  subject,
  onProgramClick,
  submittedResults = [],
  showPopupExperience = false,
  showComparisonCheckbox = true,
  lead,
  partnerIdentifiers,
}) => {
  const resultsPageConfig = useResultsPageConfigOptional();
  const disclosureCopy = resultsPageConfig?.getRecommenderModalCopy();

  if (!results || results.length === 0) {
    return null;
  }

  const sectionTitle = `Your Top Matches For ${subject}`;

  return (
    <Section
      as='section'
      variant='default'
      padding='none'
      rounded='lg'
      className='not-prose mb-2xl border-2 border-tertiary-400 bg-tertiary-50'
    >
      <Flex direction='column' gap='lg' width='full' className='p-md md:p-xl'>
        <Flex
          direction='column'
          direction-lg='row'
          justify='between'
          align='start'
          gap='md'
          width='full'
        >
          <SectionHeader
            size='h2'
            title={sectionTitle}
            className='min-w-0 flex-1'
          />
          <SponsoredResultsPopover
            spanText='Learn More About Our Partners'
            disclosureTitle={disclosureCopy?.disclosureTitle}
            disclosureContent={disclosureCopy?.disclosureContent}
          />
        </Flex>

        <Grid columns={1} columns-lg={2} gap='lg' className='w-full'>
          {results.slice(0, 2).map((result, index) => {
            const isProgramSubmitted = submittedResults.some(
              (submission) =>
                submission.programId === result.programId &&
                submission.schoolId === result.schoolId,
            );

            return (
              <Card
                onProductClick={(corr) =>
                  onProgramClick({ ...result, ...corr })
                }
                key={`${result.programId}-${result.schoolName}-${result.programName}`}
                schoolName={result.schoolName}
                schoolId={result.schoolId}
                programName={result.programName}
                schoolSlug={result.schoolSlug}
                city={result.city}
                state={result.state}
                position={index + 1}
                snippet={result.snippet}
                location={EventingLocation.EMBEDDED_RECOMMENDER}
                isProgramSubmitted={isProgramSubmitted}
                applicationCtaLabel={result.applicationCtaLabel}
                applicationCtaURL={result.applicationCtaURL}
                applicationCtaFieldMap={result.applicationCtaFieldMap}
                applicationCtaStaticParams={result.applicationCtaStaticParams}
                applicationCtaMissingValue={result.applicationCtaMissingValue}
                lead={lead}
                partnerIdentifiers={partnerIdentifiers}
                showPopupExperience={showPopupExperience}
                showComparisonCheckbox={showComparisonCheckbox}
                degreeSlug={result.degreeSlug}
                categorySlug={result.categorySlug}
                subjectSlug={result.subjectSlug}
                capId={result.capId}
                programId={result.programId}
                offeringId={result.offeringId}
                offeringUuid={result.offeringUuid}
                organizationId={result.organizationId}
                tcpa={result.tcpa}
                listId={result.listId}
                isUserRequested={result.isUserRequested}
              />
            );
          })}
        </Grid>
      </Flex>
    </Section>
  );
};

export default EmbeddedRecommender;
